/* eslint-disable react-hooks/exhaustive-deps */
import useAxios from '../../../hooks/useAxios';
import { API_ENDPOINTS } from '../../../constants/api';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toaster } from '../../../utils/toast';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import useLocationData from '../../../hooks/useLocationData';
import { UserStatus, ROLE } from '../../../utils/enums';

const LOCATION_SCOPED_ROLES = [ROLE.LOCATION_ADMIN, ROLE.LOCATION_STAFF];

interface UseUserHook {
    formik: any;
    countries: any
    states: any
    cities: any
    fetchCities: any
    fetchStates: any
    id: string | undefined
    isLoading: boolean
}

const useUser = (): UseUserHook => {
    const { countries, states, cities, fetchCities, fetchStates } = useLocationData()
    const [userData, setUserData] = useState<any>({});
    const { postData, fetchData, isLoading } = useAxios()
    const navigate = useNavigate();
    const { id } = useParams();

    useEffect(() => {
        if (id) fetchUser()
    }, [id])

    useEffect(() => {
        if (id && userData && Object.keys(userData).length > 0) {
            formik.setValues({
                first_name: userData?.first_name ?? '',
                last_name: userData?.last_name ?? '',
                email: userData?.email ?? '',
                phone_number: userData?.phone_number ?? '',
                address: userData?.address ?? '',
                zipcode: userData?.zipcode ?? '',
                country_id: userData?.country_id ?? '',
                state_id: userData?.state_id ?? '',
                city: userData?.city ?? '',
                status_id: userData?.is_active === true ? UserStatus.ACTIVE : UserStatus.DEACTIVE,
                role_id: userData?.role_id ?? '',
                access_grants: (userData?.access_grants && userData.access_grants.length > 0)
                    ? userData.access_grants.map((g: any) => ({
                        location_id: String(g.location_id),
                        send_permission: Boolean(g.send_permission)
                      }))
                    : (LOCATION_SCOPED_ROLES.includes(Number(userData?.role_id))
                        ? [{ location_id: '', send_permission: false }]
                        : []),
            });
        }
    }, [userData]);    

    const formik = useFormik({
        initialValues: {
            first_name: '',
            last_name: '',
            email: '',
            phone_number: '',
            address: '',
            zipcode: '',
            country_id: '',
            state_id: '',
            city: '',
            status_id: '',
            role_id: '',
            access_grants: [] as { location_id: string; send_permission: boolean }[],
        },
        validationSchema: Yup.object().shape({
            email: Yup.string().email('Invalid email').required('Email is required'),
            first_name: Yup.string().required('First Name is required').matches(/^[A-Za-z\s]+$/, "First Name must contain only alphabets"),
            last_name: Yup.string().required('Last Name is required').matches(/^[A-Za-z\s]+$/, "Last Name must contain only alphabets"),
            address: Yup.string().required('Address is required'),
            zipcode: Yup.string().matches(/^\d{5}$/, 'Zipcode must be a number of 5 digits').required('Zipcode is required'),
            country_id: Yup.string().required('Country is required'),
            state_id: Yup.string().required('State is required'),
            city: Yup.string().required('City is required'),
            status_id: Yup.string().required('Status is required'),
            role_id: Yup.string().required('Role is required'),
            access_grants: Yup.array().when('role_id', {
                is: (role_id: any) => LOCATION_SCOPED_ROLES.includes(Number(role_id)),
                then: (schema) => schema.of(
                    Yup.object().shape({
                        location_id: Yup.string().required('Location is required'),
                    })
                )
                .min(1, 'At least one location is required')
                .test('single-location-for-staff', 'Location Staff can only be assigned to one location', function (val) {
                    const roleId = Number(this.parent.role_id);
                    if (roleId === ROLE.LOCATION_STAFF && val && val.length > 1) {
                        return false;
                    }
                    return true;
                })
                .test('unique-locations', 'Locations must be unique', function (val) {
                    if (!val) return true;
                    const ids = val.map((g: any) => g.location_id).filter(Boolean);
                    return new Set(ids).size === ids.length;
                }),
                otherwise: (schema) => schema.notRequired(),
            }),
        }),
        onSubmit: async (values, actions) => {
            handelSubmit(values)
        },
    });

    useEffect(() => {
        if (formik.values.country_id) {
          fetchStates(formik.values.country_id)
        }
      }, [formik.values.country_id])

    // useEffect(() => {
    //     if (formik.values.state_id) {
    //       fetchCities(formik.values.state_id)
    //     }
    //   }, [formik.values.state_id])

    const handelSubmit = async (values: any) => {
        try {
            const { first_name, last_name, email, phone_number, address, state_id, country_id, zipcode, city, role_id, status_id, access_grants } = values
            const cleanGrants = LOCATION_SCOPED_ROLES.includes(Number(role_id))
                ? (Number(role_id) === ROLE.LOCATION_STAFF ? (access_grants || []).slice(0, 1) : access_grants)
                : [];
            let payload = {
                first_name, last_name, phone_number, is_active: status_id === UserStatus.ACTIVE ? true: false, role_id,
                address, state_id, country_id, zipcode, city,
                access_grants: cleanGrants,
                ...(!id && {
                    email,
                }),
                id
            }
            const response = await postData(API_ENDPOINTS.ADD_EDIT_USER, payload)
            const { message, success } = response
            if (success) {
                navigate(-1)
            }
            toaster({ message, success })
        } catch (error) {
            toaster({ message: 'Something went wrong', success: false })
        }
    }

    const fetchUser = async () => {
        const response = await fetchData(`${API_ENDPOINTS.GET_USER_INFO}/${id}`)
        if (response.success) {
            setUserData(response?.data ?? {})
        }
    }

    return {
        formik,
        id,
        countries, states, cities, fetchCities, fetchStates, isLoading
    };
};

export default useUser;
